import { useEffect, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import RestaurantCard from "../components/RestaurantCard";
import PageBanner from "../components/PageBanner";
import CitySearch from "../components/CitySearch";
import ImageCarouselModal from "../components/ImageCarouselModal";
import { fetchRestaurants } from "../services/api";

function Restaurants() {

  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(false);

  const [lat, setLat] = useState(null);
  const [lng, setLng] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 16;
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // Gallery state
  const [openPhotos, setOpenPhotos] = useState(false);
  const [currentPhotos, setCurrentPhotos] = useState([]);

  const openGallery = (restaurant) => {
    // Use photos from restaurant data (persisted in DB) or fallback to single imageUrl
    const photos = restaurant.photos && restaurant.photos.length > 0 ? restaurant.photos : [restaurant.imageUrl];
    setCurrentPhotos(photos);
    setOpenPhotos(true);
  };

  const closeGallery = () => {
    setOpenPhotos(false);
    setCurrentPhotos([]);
  };

  // 🔹 Load restaurants from API
  const loadRestaurants = (page = 1, latitude = lat, longitude = lng) => {

    if (!latitude || !longitude) return;

    setLoading(true);

    fetchRestaurants({
      lat: latitude,
      lng: longitude,
      page,
      limit: itemsPerPage
    })
      .then((res) => {

        setRestaurants(res.data.data);
        setTotalPages(res.data.pagination.total_pages);
        setTotalRecords(res.data.pagination.total_records);
        setCurrentPage(page);

        window.scrollTo({ top: 0, behavior: "smooth" });

        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  // 🔹 Get user location on first load
  useEffect(() => {

    if (navigator.geolocation) {

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLat = position.coords.latitude;
          const userLng = position.coords.longitude;

          setLat(userLat);
          setLng(userLng);

          loadRestaurants(1, userLat, userLng);
        },
        () => {
          // If user denies location → fallback to default (Delhi)
          const defaultLat = 28.6139;
          const defaultLng = 77.2090;

          setLat(defaultLat);
          setLng(defaultLng);

          loadRestaurants(1, defaultLat, defaultLng);
        }
      );

    }

  }, []);

  return (
    <div className="bg-white min-h-screen">

      <Header />

      {/* Search Section */}
      <PageBanner
        title="Discover Premium Restaurants"
        subtitle="Find exceptional dining experiences near you."
        image="https://images.unsplash.com/photo-1579584425555-c3ce17fd4351?auto=format&fit=crop&w=1920&q=80"
      >

        {/* Search Box - Frosted Glass Effect on Banner */}
        <div className="bg-white/95 backdrop-blur-md shadow-2xl rounded-xl p-5 md:p-6 flex flex-col md:flex-row items-center gap-4 border border-white/30">

          <CitySearch
            onSelect={(selectedLat, selectedLng) => {
              setLat(selectedLat);
              setLng(selectedLng);
              loadRestaurants(1, selectedLat, selectedLng);
            }}
          />

          <button
            onClick={() => loadRestaurants(1)}
            className="brand-orange-bg text-white px-8 py-3 rounded-lg hover:opacity-90 transition"
          >
            Search
          </button>

        </div>

      </PageBanner>


      {/* Results Section */}
      <section className="pt-20 pb-24">

        <div className="max-w-7xl mx-auto px-4">

          {loading ? (
            <p className="text-center text-gray-500">Loading restaurants...</p>
          ) : (
            <>
              <div className="mb-8 text-gray-600 text-sm">
                Showing{" "}
                <span className="font-semibold">
                  {(currentPage - 1) * itemsPerPage + 1}
                </span>{" "}
                to{" "}
                <span className="font-semibold">
                  {Math.min(currentPage * itemsPerPage, totalRecords)}
                </span>{" "}
                of{" "}
                <span className="font-semibold">{totalRecords}</span> restaurants
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {restaurants.map((restaurant) => (
                  <RestaurantCard key={restaurant.id} restaurant={{...restaurant, userLat: lat, userLng: lng}} onOpenGallery={openGallery} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex justify-center mt-16 space-x-2">

                  <button
                    disabled={currentPage === 1}
                    onClick={() => loadRestaurants(currentPage - 1)}
                    className="px-4 py-2 rounded-lg border text-sm disabled:opacity-40"
                  >
                    Prev
                  </button>

                  {[...Array(totalPages)].map((_, index) => (
                    <button
                      key={index}
                      onClick={() => loadRestaurants(index + 1)}
                      className={`px-4 py-2 rounded-lg text-sm ${
                          currentPage === index + 1
                            ? "brand-orange-bg text-white"
                            : "border"
                        }`}
                    >
                      {index + 1}
                    </button>
                  ))}

                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => loadRestaurants(currentPage + 1)}
                    className="px-4 py-2 rounded-lg border text-sm disabled:opacity-40"
                  >
                    Next
                  </button>

                </div>
              )}
            </>
          )}

        </div>

      </section>

      {openPhotos && (
        <ImageCarouselModal photos={currentPhotos} initialIndex={0} onClose={closeGallery} />
      )}

      <Footer />

    </div>
  );
}

export default Restaurants;
